import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input, Select, Table, Tag, Typography } from 'antd';
import { useFetchData } from '../../hooks';
import { TrackingRequestRow } from '../../types';
import StatusTag from '../../components/ui/StatusTag';
import PageHeaderAction from '../../components/PageHeaderAction';
import { convertDate, dateFormat } from '../../lib/utils';

const { Text } = Typography;

const stageColour: Record<TrackingRequestRow['stage'], string> = {
  RAISED: 'default',
  CANVASSED: 'gold',
  ORDERED: 'processing',
  RECEIVED: 'cyan',
  DELIVERED: 'green',
};

const stageOptions = (
  ['RAISED', 'CANVASSED', 'ORDERED', 'RECEIVED', 'DELIVERED'] as const
).map((s) => ({ label: s, value: s }));

/**
 * Where every open request sits in the BOM -> PR -> Canvass -> PO -> RR -> DR
 * flow. One row per request; the stage is the furthest step it has reached.
 */
const TrackingPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<string | undefined>(undefined);

  const { data, isLoading } = useFetchData<TrackingRequestRow[]>(
    '/tracking/requests',
    { search, stage: stage || undefined },
  );

  const columns = [
    {
      title: 'PR Code',
      dataIndex: 'code',
      render: (_text: string, record: TrackingRequestRow) => (
        <Link to={`/purchase-request/${record.id}?action=view`}>
          <span className={'text-blue-400 font-semibold'}>{record.code}</span>
        </Link>
      ),
    },
    {
      title: 'Requested By',
      dataIndex: 'requestedBy',
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      render: (v: string | null) => (v ? convertDate(v, dateFormat) : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_text: string, record: TrackingRequestRow) => (
        <StatusTag status={record.status} />
      ),
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      render: (_text: string, record: TrackingRequestRow) => (
        <Tag color={stageColour[record.stage]}>{record.stage}</Tag>
      ),
    },
    {
      title: 'Stage Date',
      dataIndex: 'stageDate',
      render: (v: string | null) => (v ? convertDate(v, dateFormat) : '—'),
    },
    {
      title: 'Days in Stage',
      dataIndex: 'daysInStage',
      render: (v: number | null) =>
        v === null ? (
          '—'
        ) : (
          <Text type={v >= 7 ? 'danger' : undefined}>{v}</Text>
        ),
    },
    {
      title: 'Counts',
      key: 'counts',
      render: (_text: unknown, record: TrackingRequestRow) => (
        <span className={'text-xs whitespace-nowrap'}>
          C:{record.counts.canvass} PO:{record.counts.po} RR:
          {record.counts.rr} DR:{record.counts.dr}
        </span>
      ),
    },
  ];

  return (
    <div className={'page-wrapper'}>
      <PageHeaderAction moduleName={'Request Tracking'} action={null} />
      <div className={'flex gap-2 py-2'}>
        <Input.Search
          placeholder={'PR code, PO code, or production order'}
          allowClear
          onSearch={(v) => setSearch(v)}
          style={{ maxWidth: 360 }}
        />
        <Select
          placeholder={'Stage'}
          allowClear
          options={stageOptions}
          value={stage}
          onChange={(v) => setStage(v)}
          style={{ minWidth: 180 }}
        />
      </div>
      <Table<TrackingRequestRow>
        rowKey={(record) => record.id}
        dataSource={data ?? []}
        loading={isLoading}
        columns={columns}
        size={'small'}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onRow={(record) => ({
          onClick: () => navigate(`/purchase-request/${record.id}`),
          className: 'cursor-pointer',
        })}
      />
    </div>
  );
};

export default TrackingPage;
